import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api, useAuth, formatApiError } from "../lib/api";
import { landingFor } from "../lib/roles";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LockKey, CircleNotch } from "@phosphor-icons/react";

/**
 * Camouflaged sign-in gate.
 * No SoverenX branding visible to unauthenticated visitors — just a plain, secure-area style form.
 * Flow: enter email + password → server sends 6-digit code to email → enter code → in.
 */
export default function Login() {
  const { user, loading, setUser } = useAuth();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState("credentials");  // credentials | code | forgot | forgot_sent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/auth/config").then(r => setConfig(r.data)).catch(() => {}); }, []);

  if (!loading && user) return <Navigate to={landingFor(user.role)} replace />;

  const submitForgot = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setBusy(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setStep("forgot_sent");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Request failed."));
    } finally { setBusy(false); }
  };

  const submitCredentials = async (e) => {
    e?.preventDefault();
    if (!email || !password) return;
    setBusy(true); setError("");
    try {
      // Only bootstrap (first-ever admin) uses signup path from /login.
      // Invited users MUST go via /signup (from their email link) — never auto-created here.
      const isBootstrap = config?.bootstrap_available;
      const endpoint = isBootstrap ? "/auth/signup" : "/auth/login";
      const payload = isBootstrap ? { email, password, name: email.split("@")[0] } : { email, password };
      const res = await api.post(endpoint, payload);
      setDevCode(res.data.dev_code || null);
      setStep("code");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Invalid credentials."));
    } finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    setBusy(true); setError("");
    try {
      const res = await api.post("/auth/verify", { email, code });
      setUser(res.data.user);
      toast.success("Signed in");
      nav(landingFor(res.data.user.role), { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Invalid or expired code."));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/resend-code", { email, password });
      setDevCode(res.data.dev_code || null);
      toast.success("New code sent");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center">
            <LockKey size={17} className="text-slate-600" />
          </div>
          <div>
            <div className="text-sm font-medium text-[#0F1E38]">Restricted Area</div>
            <div className="text-[11px] text-slate-500">Sign in to continue</div>
          </div>
        </div>

        {step === "credentials" && (
          <form onSubmit={submitCredentials} className="space-y-4">
            <div>
              <Label className="text-xs text-slate-600">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-11"
                required
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-11"
                required
                minLength={8}
              />
              {config?.bootstrap_available && (
                <div className="text-[10.5px] text-slate-500 mt-1.5 font-mono">
                  First-time setup — minimum 8 characters
                </div>
              )}
            </div>
            {error && <div data-testid="login-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-[#0F1E38] hover:bg-[#1E3A5F] text-white font-medium"
            >
              {busy ? <CircleNotch size={16} className="animate-spin" /> : "Continue"}
            </Button>
            {!config?.bootstrap_available && (
              <div className="text-center">
                <button type="button" data-testid="forgot-link" onClick={() => { setStep("forgot"); setError(""); }} className="text-[11px] text-slate-500 hover:text-[#0F1E38]" style={{transition:"color 0.15s ease"}}>Forgot password?</button>
              </div>
            )}
          </form>
        )}

        {step === "forgot" && (
          <form onSubmit={submitForgot} className="space-y-4">
            <div>
              <div className="text-sm text-[#0F1E38] font-medium">Reset your password</div>
              <div className="text-xs text-slate-500 mt-1">Enter your email. If an account exists, we'll email you a reset link.</div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Email</Label>
              <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" required autoFocus/>
            </div>
            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
            <Button data-testid="forgot-submit" type="submit" disabled={busy} className="w-full h-11 bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold">
              {busy ? <CircleNotch size={16} className="animate-spin" /> : "Send reset link"}
            </Button>
            <button type="button" onClick={() => { setStep("credentials"); setError(""); }} className="w-full text-center text-[11px] text-slate-500 hover:text-[#0F1E38]" style={{transition:"color 0.15s ease"}}>← Back to sign in</button>
          </form>
        )}

        {step === "forgot_sent" && (
          <div className="py-4">
            <div className="text-sm text-[#0F1E38] font-medium">Check your inbox</div>
            <div className="text-xs text-slate-500 mt-2 leading-relaxed">
              If an account exists for that email, a reset link will arrive within a minute. The link expires in 1 hour and can be used only once.
            </div>
            <Button onClick={() => { setStep("credentials"); setEmail(""); }} data-testid="forgot-done" className="w-full mt-6 h-11 bg-[#0F1E38] hover:bg-[#1E3A5F] text-white">Back to sign in</Button>
          </div>
        )}

        {step === "code" && (
          <form onSubmit={submitCode} className="space-y-4">
            <div>
              <div className="text-sm text-[#0F1E38] font-medium">Enter verification code</div>
              <div className="text-xs text-slate-500 mt-1">A 6-digit code was sent to <span className="text-[#0F1E38] font-mono">{email}</span></div>
            </div>
            {devCode && (
              <div className="text-[11px] font-mono bg-amber-50 border border-amber-200 text-amber-900 p-2 rounded">
                <span className="font-semibold">DEV MODE</span> — code: <span className="font-bold">{devCode}</span>
                <div className="text-[10px] mt-1">(set RESEND_API_KEY to send real emails)</div>
              </div>
            )}
            <Input
              data-testid="login-code"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="h-14 text-center text-2xl font-mono tracking-[0.4em]"
              autoFocus
              placeholder="——————"
            />
            {error && <div data-testid="login-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
            <Button
              data-testid="verify-submit"
              type="submit"
              disabled={busy || code.length !== 6}
              className="w-full h-11 bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold"
            >
              {busy ? <CircleNotch size={16} className="animate-spin" /> : "Verify & Sign in"}
            </Button>
            <div className="flex justify-between text-[11px] pt-1">
              <button type="button" onClick={() => { setStep("credentials"); setCode(""); setError(""); }} className="text-slate-500 hover:text-[#0F1E38]" style={{transition:"color 0.15s ease"}}>← Change email</button>
              <button type="button" onClick={resend} disabled={busy} className="text-slate-500 hover:text-[#0F1E38]" style={{transition:"color 0.15s ease"}}>Resend code</button>
            </div>
          </form>
        )}

        <div className="mt-8 pt-4 border-t border-slate-100 text-[10px] text-slate-400 text-center font-mono uppercase tracking-wider">
          Authorised access only
        </div>
      </div>
    </div>
  );
}
